import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, loading, format_date, toFixed, Select } from "../components/CustomComponent";
import PayslipDocument, { downloadPayslipPdf } from "../components/PayslipDocument";
import PersonalTaxPanel from "../components/PersonalTaxPanel";
import FinancialPlanPanel from "../components/FinancialPlanPanel";
import PositionBadge from "../components/PositionBadge";
import { getBirthdayInfo, BirthdayDecor, BirthdayBadge, BIRTHDAY_HERO_BACKGROUND, BIRTHDAY_HERO_SHADOW, BIRTHDAY_GLOW } from "../components/birthday";

const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  borderRadius: 24,
  boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)",
};

// พื้นหลัง hero ชุดเดียวกับหน้าแรก (Welcome) เพื่อให้หัวหน้าเว็บดูเป็นชุดเดียวกัน
// ธีมวันเกิดใช้ค่าจาก components/birthday.js ตัวเดียวกับหน้าแรก
const DEFAULT_HERO_BACKGROUND =
  "radial-gradient(circle at top right, rgba(47, 213, 228, 0.24), transparent 26%), linear-gradient(135deg, #08233d 0%, #0d4d83 52%, #11a4bf 100%)";

const DEFAULT_HERO_SHADOW = "0 28px 60px rgba(8, 35, 61, 0.22)";

const DEFAULT_GLOW = "radial-gradient(circle, rgba(88, 229, 255, 0.20) 0%, rgba(88, 229, 255, 0) 72%)";

const TABS = [
  { value: "info", label: "ข้อมูลส่วนตัว", icon: "fa-id-card" },
  { value: "attendance", label: "ประวัติเข้างาน", icon: "fa-clock" },
  { value: "leave", label: "วันลาของฉัน", otherLabel: "วันลา", icon: "fa-calendar-check" },
  { value: "payslip", label: "สลิปเงินเดือน", icon: "fa-file-invoice-dollar" },
  { value: "tax", label: "ภาษีส่วนบุคคล", icon: "fa-receipt" },
  // วางแผนการเงินเป็นเรื่องส่วนตัว เปิดให้เฉพาะตอนดูโปรไฟล์ของตัวเอง (selfOnly) และเฉพาะบัญชี super_user (superOnly)
  { value: "finance-plan", label: "วางแผนการเงิน", icon: "fa-piggy-bank", selfOnly: true, superOnly: true },
];

// ตำแหน่งที่เลือกดูโปรไฟล์ของพนักงานคนอื่นได้ (backend ตรวจซ้ำอีกชั้นจากตำแหน่งจริงในฐานข้อมูล)
const CEO_POSITION = "CEO";

const getEmployeeName = (item) =>
  `${item?.prefix_name || ""}${item?.first_name || ""} ${item?.last_name || ""}`.trim() || item?.nickname || item?.employee_code || "-";

const LEAVE_STATUS_STYLE = {
  pending: { background: "rgba(245, 166, 35, 0.16)", color: "#b97007", label: "รออนุมัติ" },
  approved: { background: "rgba(47, 181, 114, 0.14)", color: "#188251", label: "อนุมัติแล้ว" },
  rejected: { background: "rgba(229, 83, 83, 0.16)", color: "#b63636", label: "ไม่อนุมัติ" },
  cancelled: { background: "rgba(108, 122, 137, 0.14)", color: "#566575", label: "ยกเลิก" },
};

const getProfile = () => {
  try {
    const raw = localStorage.getItem("ptn-profile");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" });
};

export default function MyProfile() {
  const profile = useMemo(() => getProfile(), []);
  const employee = profile?.employee || null;

  // ถ้าวันนี้เป็นวันเกิด หัวการ์ดจะเปลี่ยนเป็นธีมปาร์ตี้เหมือนหน้าแรก
  const { isBirthday, birthdayAge, birthdayWish } = useMemo(() => getBirthdayInfo(profile), [profile]);

  // CEO เลือกดูข้อมูลของพนักงานคนอื่นได้ selectedUid ว่าง = ดูของตัวเอง
  const isCeo = (employee?.position || "").trim().toUpperCase() === CEO_POSITION;
  // super_user = ประเภทบัญชีจาก profile ที่ล็อกอิน (เงื่อนไขเดียวกับหน้าไทม์ไลน์โปรเจกต์)
  const isSuperUser = profile?.user_type === "super_user";
  const [employees, setEmployees] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");

  const isSelf = !selectedUid || selectedUid === employee?.uid;
  // ข้อมูลพนักงานที่กำลังแสดง: ของตัวเองจาก profile ที่ล็อกอิน / ของคนอื่นจากรายชื่อที่โหลดมา
  const viewEmployee = useMemo(
    () => (isSelf ? employee : employees.find((item) => item.uid === selectedUid) || null),
    [isSelf, employee, employees, selectedUid]
  );
  const viewUid = viewEmployee?.uid || null;

  const [activeTab, setActiveTab] = useState("info");
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState({ types: [] });
  const [payslips, setPayslips] = useState([]);
  const [company, setCompany] = useState(null);
  const [previewSlip, setPreviewSlip] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const slipRef = useRef(null);

  // รายชื่อพนักงานสำหรับ CEO เลือกดู (เส้น /basic ไม่มีข้อมูลเงินเดือน/บัญชีธนาคาร)
  useEffect(() => {
    if (!isCeo) return;
    let cancelled = false;
    (async () => {
      const result = await GET("v1/admin/employees/basic");
      if (!cancelled && result?.status) setEmployees(result.data || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [isCeo]);

  // เปลี่ยนคนที่ดู ให้ล้างข้อมูลของคนก่อนหน้าทิ้ง จะได้ไม่เห็นข้อมูลคนอื่นค้างระหว่างรอโหลด
  useEffect(() => {
    setAttendance([]);
    setLeaves([]);
    setLeaveSummary({ types: [] });
    setPayslips([]);
    setPreviewSlip(null);
  }, [selectedUid]);

  // แท็บที่มีเงื่อนไข (เช่น วางแผนการเงิน: เฉพาะตัวเอง + super_user) ถ้าเงื่อนไขไม่ผ่านให้กลับไปแท็บข้อมูลส่วนตัว
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => (isSelf || !tab.selfOnly) && (isSuperUser || !tab.superOnly)),
    [isSelf, isSuperUser]
  );
  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab)) setActiveTab("info");
  }, [visibleTabs, activeTab]);

  const loadTabData = useCallback(async () => {
    if (!viewUid) return;

    // ดูของตัวเองไม่ต้องส่ง employee_id (backend ผูกกับบัญชีที่ล็อกอินให้เอง) ดูของคนอื่นส่ง uid ไปด้วย
    const targetParams = isSelf ? {} : { employee_id: viewUid };

    loading(true);
    if (activeTab === "attendance") {
      const result = await GET("v1/internal/attendance", {
        employee_id: viewUid,
        page: 1,
        page_size: 60,
      });
      if (result?.status) setAttendance(result.data || []);
    }

    if (activeTab === "leave") {
      const [listResult, summaryResult] = await Promise.all([
        GET("v1/admin/leaves", { page: 0, year, ...targetParams }),
        GET("v1/admin/leaves/summary", { year, ...targetParams }),
      ]);
      if (listResult?.status) setLeaves(listResult.data || []);
      if (summaryResult?.status && summaryResult.data) setLeaveSummary(summaryResult.data);
    }

    if (activeTab === "payslip") {
      const [slipResult, companyResult] = await Promise.all([
        GET("v1/admin/payrolls/my-payslips", isSelf ? undefined : targetParams),
        // ใช้เส้น /basic เพราะพนักงานทั่วไปไม่มีสิทธิ์ companies.read (เส้นเต็มจะได้ 403)
        GET("v1/admin/companies/basic"),
      ]);
      if (slipResult?.status) setPayslips(slipResult.data || []);
      if (companyResult?.status) setCompany(companyResult.data || null);
    }
    loading(false);
  }, [activeTab, viewUid, isSelf, year]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const employeeOptions = useMemo(
    () =>
      employees.map((item) => ({
        value: item.uid,
        label: `${getEmployeeName(item)}${item.employee_code ? ` (${item.employee_code})` : ""}${item.uid === employee?.uid ? " - ตัวเอง" : ""}`,
      })),
    [employees, employee]
  );

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 4 }, (_, index) => String(current - index));
  }, []);

  const fullName = viewEmployee
    ? getEmployeeName(viewEmployee)
    : profile?.full_name_th || "-";

  // ธีมวันเกิดใช้เฉพาะตอนดูโปรไฟล์ตัวเอง
  const showBirthday = isBirthday && isSelf;

  // แต่ละหัวข้อมีไอคอนและสีประจำตัว ใช้เป็นแถบเน้นซ้ายการ์ดและสีเงาตอน hover
  const infoRows = [
    { label: "รหัสพนักงาน", value: viewEmployee?.employee_code, icon: "fa-hashtag", accent: "#0c76e5" },
    { label: "ชื่อเล่น", value: viewEmployee?.nickname, icon: "fa-face-smile", accent: "#d24a6e" },
    { label: "แผนก", value: viewEmployee?.department, icon: "fa-sitemap", accent: "#0e93a7" },
    { label: "ตำแหน่ง", value: viewEmployee?.position, icon: "fa-briefcase", accent: "#a3781a", render: () => <PositionBadge position={viewEmployee?.position} /> },
    // อีเมล/เบอร์โทร: ของตัวเองใช้ของบัญชีผู้ใช้ก่อน ถ้าไม่มีค่อยใช้ของข้อมูลพนักงาน / ของคนอื่นใช้ข้อมูลพนักงาน
    { label: "อีเมล", value: isSelf ? profile?.email || employee?.email : viewEmployee?.email, icon: "fa-envelope", accent: "#5b6ed6" },
    { label: "เบอร์โทร", value: isSelf ? profile?.phone || employee?.phone : viewEmployee?.phone, icon: "fa-phone", accent: "#1f9a63" },
    { label: "วันเกิด", value: viewEmployee?.birthday ? format_date(viewEmployee.birthday, "dd/mm/yyyy") : null, icon: "fa-cake-candles", accent: "#e0568a" },
    { label: "เวลาทำงาน", value: viewEmployee?.working_start_time && viewEmployee?.working_end_time ? `${viewEmployee.working_start_time} - ${viewEmployee.working_end_time}` : null, icon: "fa-business-time", accent: "#7a55c9" },
    { label: "บทบาทในระบบ", value: isSelf ? profile?.role?.name : viewEmployee?.role?.name, icon: "fa-user-shield", accent: "#4a6076" },
  ];

  const handleDownloadSlip = async (item) => {
    setPreviewSlip(item);
    // รอให้ DOM วาดสลิปก่อนแปลงเป็น PDF
    setTimeout(async () => {
      await downloadPayslipPdf(slipRef.current, `payslip_${item.payroll?.period_month || ""}`);
    }, 250);
  };

  return (
    <AppShell title="โปรไฟล์ของฉัน" description="ข้อมูลส่วนตัว ประวัติการเข้างาน วันลาคงเหลือ สลิปเงินเดือน สรุปภาษีส่วนบุคคล และวางแผนการเงินของคุณ">
      <style>{`
        /* ===== ชิปแผนกบน hero (แก้วฝ้า) ===== */
        .ptn-hero-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.34rem 0.85rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 600;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.28);
          backdrop-filter: blur(6px);
        }

        /* ===== แถบเลือกพนักงาน (เฉพาะ CEO) =====
           z-index ต้องสูงกว่าทุกอย่างในเนื้อหาด้านล่าง เพื่อให้รายการ dropdown ของ Select ไม่ถูกทับ
           - แท็บ/การ์ดข้อมูล (ไม่มี z-index)
           - แถบบันทึกลอย .ptn-plan__savebar ในแท็บวางแผนการเงิน (sticky, z-index 40)
           - แถบหัว/ฟอร์มใน FinancialPlanPanel (30/20) และ dropdown ใน IncomeDetail (30)
           แต่ยังต่ำกว่า navbar/sidebar (1035+) และ modal (1055) */
        .ptn-picker {
          position: relative;
          z-index: 100;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.85rem 1rem;
          padding: 0.95rem 1.1rem;
          border-radius: 20px;
          background: linear-gradient(135deg, #fffaea 0%, #fff3d3 100%);
          border: 1px solid rgba(184, 138, 32, 0.30);
          box-shadow: 0 10px 24px rgba(184, 138, 32, 0.12);
        }
        .ptn-picker__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          border-radius: 14px;
          color: #8a6212;
          background: rgba(212, 165, 55, 0.18);
          border: 1px solid rgba(184, 138, 32, 0.30);
        }
        .ptn-picker__text { flex: 1 1 220px; min-width: 0; }
        .ptn-picker__label { font-weight: 700; color: #6e4d0c; }
        .ptn-picker__hint { font-size: 0.78rem; color: #8a6e35; }
        .ptn-picker__select { flex: 1 1 260px; max-width: 360px; }
        .ptn-picker__select .form-control {
          border-radius: 12px;
          border-color: rgba(184, 138, 32, 0.35);
          background: #ffffff;
        }
        .ptn-picker__reset {
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.9rem;
          border-radius: 999px;
          border: 1px solid rgba(184, 138, 32, 0.40);
          background: #ffffff;
          color: #8a6212;
          font-size: 0.82rem;
          font-weight: 600;
          transition: background 0.2s ease;
        }
        .ptn-picker__reset:hover { background: rgba(212, 165, 55, 0.14); }

        /* ===== แท็บแบบพรีเมี่ยม ===== */
        .ptn-tab {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.55rem 1.1rem;
          border-radius: 999px;
          font-weight: 600;
          font-size: 0.92rem;
          color: #35536e;
          background: #ffffff;
          border: 1px solid rgba(18, 125, 255, 0.14);
          box-shadow: 0 4px 12px rgba(12, 118, 229, 0.05);
          transition: transform 0.22s ease, box-shadow 0.22s ease, color 0.22s ease, background 0.22s ease;
        }
        .ptn-tab__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 999px;
          font-size: 0.76rem;
          background: rgba(12, 118, 229, 0.10);
          color: #0c76e5;
          transition: background 0.22s ease, color 0.22s ease;
        }
        .ptn-tab:hover {
          transform: translateY(-2px);
          color: #0b4f84;
          box-shadow: 0 10px 22px rgba(12, 118, 229, 0.12);
        }
        .ptn-tab--active {
          color: #ffffff;
          background: linear-gradient(135deg, #0b4f84 0%, #0c76e5 100%);
          border-color: transparent;
          box-shadow: 0 12px 26px rgba(12, 118, 229, 0.30);
        }
        .ptn-tab--active:hover { color: #ffffff; }
        .ptn-tab--active .ptn-tab__icon {
          background: rgba(255, 255, 255, 0.22);
          color: #ffffff;
        }
        /* แสงวิ่งผ่านแท็บที่เลือกอยู่ */
        .ptn-tab--active::after {
          content: "";
          position: absolute;
          top: -60%;
          left: -140%;
          width: 55%;
          height: 220%;
          background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.32) 50%, rgba(255,255,255,0) 100%);
          transform: rotate(14deg);
          animation: ptnProfileSheen 5s ease-in-out infinite;
          pointer-events: none;
        }

        /* ===== การ์ดข้อมูลส่วนตัว ===== */
        .ptn-info-card {
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          padding: 1rem 1.05rem;
          border-radius: 20px;
          background: linear-gradient(180deg, #ffffff 0%, #f5faff 100%);
          border: 1px solid rgba(18, 125, 255, 0.10);
          box-shadow: 0 6px 16px rgba(12, 118, 229, 0.05);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        /* แถบสีประจำหัวข้อด้านซ้าย */
        .ptn-info-card::before {
          content: "";
          position: absolute;
          top: 12px;
          bottom: 12px;
          left: 0;
          width: 3px;
          border-radius: 0 3px 3px 0;
          background: var(--accent, #0c76e5);
          opacity: 0.75;
        }
        .ptn-info-card::after {
          content: "";
          position: absolute;
          top: -70%;
          left: -160%;
          width: 55%;
          height: 240%;
          background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 50%, rgba(255,255,255,0) 100%);
          transform: rotate(14deg);
          transition: left 0.65s ease;
          pointer-events: none;
        }
        .ptn-info-card:hover {
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--accent, #0c76e5) 32%, transparent);
          box-shadow: 0 16px 30px color-mix(in srgb, var(--accent, #0c76e5) 18%, transparent);
        }
        .ptn-info-card:hover::after { left: 170%; }
        .ptn-info-card__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 38px;
          height: 38px;
          border-radius: 13px;
          font-size: 0.92rem;
          color: var(--accent, #0c76e5);
          background: color-mix(in srgb, var(--accent, #0c76e5) 12%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--accent, #0c76e5) 22%, transparent);
          transition: transform 0.25s ease;
        }
        .ptn-info-card:hover .ptn-info-card__icon { transform: scale(1.08) rotate(-4deg); }
        .ptn-info-card__body { min-width: 0; }
        .ptn-info-card__label {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #8299ab;
          margin-bottom: 0.25rem;
        }
        .ptn-info-card__value {
          font-weight: 700;
          color: #12314d;
          word-break: break-word;
        }

        /* ===== การ์ดสรุปวันลา ===== */
        .ptn-leave-card {
          position: relative;
          overflow: hidden;
          padding: 1rem 1.05rem;
          border-radius: 20px;
          background: linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%);
          border: 1px solid rgba(18, 125, 255, 0.10);
          box-shadow: 0 6px 16px rgba(12, 118, 229, 0.05);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .ptn-leave-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 30px rgba(12, 118, 229, 0.14);
        }
        .ptn-leave-card__label {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #8299ab;
          margin-bottom: 0.2rem;
        }
        .ptn-leave-card__value {
          font-size: 1.5rem;
          font-weight: 800;
          line-height: 1.2;
          color: #0c76e5;
        }
        .ptn-leave-card__value span {
          font-size: 0.82rem;
          font-weight: 500;
          color: #7d94a6;
          margin-left: 0.25rem;
        }
        .ptn-leave-bar {
          height: 6px;
          border-radius: 999px;
          background: rgba(12, 118, 229, 0.10);
          overflow: hidden;
          margin: 0.55rem 0 0.4rem;
        }
        .ptn-leave-bar > span {
          display: block;
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, #0c76e5 0%, #2fd5e4 100%);
          box-shadow: 0 0 10px rgba(47, 213, 228, 0.55);
          transition: width 0.5s ease;
        }
        .ptn-leave-card__meta { font-size: 0.78rem; color: #7d94a6; }

        @keyframes ptnProfileSheen {
          0% { left: -140%; }
          32%, 100% { left: 170%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ptn-tab--active::after { animation: none !important; }
        }

        /* ===== มุมมองการ์ดบนมือถือ ===== */
        @media (max-width: 767px) {
          .ptn-profile-cards tbody tr {
            padding: 0 !important;
            overflow: hidden;
            border-radius: 18px !important;
            border: 1px solid rgba(18, 125, 255, 0.12) !important;
            box-shadow: 0 12px 26px rgba(12, 118, 229, 0.08) !important;
            background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%) !important;
          }
          .ptn-profile-cards td {
            padding: 0.6rem 0.95rem !important;
            font-size: 0.9rem;
            border-bottom: 1px dashed rgba(18, 125, 255, 0.12) !important;
          }
          .ptn-profile-cards td::before {
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: #8299ab;
          }
          /* แถวแรกของแต่ละการ์ด = แถบหัว (วันที่ / รหัส / งวด) */
          .ptn-profile-cards tbody td:first-child {
            background: linear-gradient(135deg, #0b4f84 0%, #0c76e5 100%);
            color: #ffffff !important;
            font-weight: 700;
            letter-spacing: 0.03em;
            border-bottom: none !important;
            padding: 0.7rem 0.95rem !important;
          }
          .ptn-profile-cards tbody td:first-child::before { color: rgba(255, 255, 255, 0.72); }
          /* แถวว่าง (ไม่พบข้อมูล) ไม่ต้องเป็นแถบหัว */
          .ptn-profile-cards tbody td[colspan] {
            display: block;
            background: transparent;
            color: #7d94a6 !important;
            font-weight: 500;
            text-align: center !important;
          }
          .ptn-profile-cards tbody td[colspan]::before { display: none; }
          /* แถบปุ่มท้ายการ์ด (สลิปเงินเดือน) */
          .ptn-profile-cards td[data-label="สลิป"] {
            display: flex;
            gap: 0.5rem;
            background: rgba(12, 118, 229, 0.05);
            border-bottom: none !important;
            padding: 0.7rem 0.95rem !important;
          }
          .ptn-profile-cards td[data-label="สลิป"]::before {
            flex: 1;
            align-self: center;
            padding-right: 0;
          }
          .ptn-profile-cards td[data-label="สลิป"] .btn {
            min-width: 46px;
            border-radius: 12px;
          }
          .ptn-profile-cards td[data-label="เงินรับสุทธิ"] { font-size: 1rem; }
        }
      `}</style>

      {/* หัวการ์ดชุดเดียวกับหน้าแรก: มุมโค้ง เงา วงกลมตกแต่ง และธีมวันเกิดเหมือนกัน */}
      <div
        className="rounded-4 rounded-xl-5 overflow-hidden position-relative p-4 p-md-5 mb-4 text-white"
        style={{
          background: showBirthday ? BIRTHDAY_HERO_BACKGROUND : DEFAULT_HERO_BACKGROUND,
          boxShadow: showBirthday ? BIRTHDAY_HERO_SHADOW : DEFAULT_HERO_SHADOW,
        }}
      >
        <div
          className="position-absolute rounded-circle"
          style={{ top: -70, right: -40, width: 240, height: 240, background: "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 72%)" }}
        />
        <div
          className="position-absolute rounded-circle"
          style={{ bottom: -90, left: -30, width: 280, height: 280, background: showBirthday ? BIRTHDAY_GLOW : DEFAULT_GLOW }}
        />

        {showBirthday ? <BirthdayDecor /> : null}

        <div className="position-relative d-flex flex-column flex-md-row align-items-md-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
            style={{ width: 76, height: 76, borderRadius: 24, background: "rgba(255,255,255,0.18)", fontSize: "1.8rem" }}
          >
            {(viewEmployee?.first_name || (isSelf ? profile?.full_name_th : "") || "-").slice(0, 1)}
          </div>
          <div className="flex-grow-1">
            {showBirthday && <BirthdayBadge birthdayAge={birthdayAge} className="mb-2" />}
            {!isSelf && (
              <span className="ptn-hero-chip mb-2" style={{ background: "rgba(255, 214, 120, 0.22)", borderColor: "rgba(255, 214, 120, 0.5)" }}>
                <i className="fa-solid fa-crown" />
                กำลังดูข้อมูลของพนักงาน
              </span>
            )}
            <h2 className="fw-bold mb-1" style={{ fontSize: "clamp(1.5rem, 3vw, 2.2rem)", ...(showBirthday ? { textShadow: "0 4px 16px rgba(120, 60, 90, 0.18)" } : {}) }}>
              {showBirthday ? `🎉 Happy Birthday, ${fullName}` : fullName}
            </h2>
            <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
              <PositionBadge position={viewEmployee?.position} onDark />
              <span className="ptn-hero-chip">
                <i className="fa-solid fa-sitemap" />
                {viewEmployee?.department || "-"}
              </span>
            </div>
            {showBirthday ? (
              <div className="mt-2" style={{ maxWidth: 560, opacity: 0.95 }}>{birthdayWish}</div>
            ) : (
              <div className="small mt-1" style={{ opacity: 0.75 }}>
                {viewEmployee?.employee_code ? `รหัส ${viewEmployee.employee_code}` : "ยังไม่ได้ผูกกับข้อมูลพนักงาน"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CEO เลือกพนักงานเพื่อดูข้อมูลรายคน (ว่าง = กลับมาดูของตัวเอง)
          วางนอก hero เพราะ hero ตั้ง overflow-hidden ไว้ จะตัดรายการ dropdown ของ Select ทิ้ง */}
      {isCeo && (
        <div className="ptn-picker mb-4">
          <span className="ptn-picker__icon"><i className="fa-solid fa-crown" /></span>
          <div className="ptn-picker__text">
            <div className="ptn-picker__label">ดูข้อมูลพนักงาน</div>
            <div className="ptn-picker__hint">เฉพาะตำแหน่ง CEO เลือกพนักงานเพื่อดูข้อมูลส่วนตัว การเข้างาน วันลา สลิป และภาษีรายคน</div>
          </div>
          <div className="ptn-picker__select">
            <Select
              className="form-control"
              value={selectedUid}
              options={employeeOptions}
              placeholder="ตัวเอง"
              onChange={(value) => setSelectedUid(value && value !== employee?.uid ? value : "")}
            />
          </div>
          {!isSelf && (
            <button type="button" className="ptn-picker__reset" onClick={() => setSelectedUid("")}>
              <i className="fa-solid fa-rotate-left me-1" />
              กลับมาดูของฉัน
            </button>
          )}
        </div>
      )}

      {!employee && isSelf && (
        <div className="p-4 mb-4 rounded-4" style={{ background: "rgba(245, 166, 35, 0.12)", border: "1px solid rgba(245, 166, 35, 0.3)", color: "#8a5300" }}>
          บัญชีนี้ยังไม่ได้ผูกกับข้อมูลพนักงาน จึงยังดูประวัติเข้างาน วันลา และสลิปเงินเดือนไม่ได้
          <br />แจ้งผู้ดูแลระบบให้ผูกบัญชีกับพนักงานที่หน้า ตั้งค่าระบบ &gt; บัญชีผู้ใช้
        </div>
      )}

      <div className="d-flex gap-2 flex-wrap scroll-x-mobile mb-4">
        {visibleTabs.map((tab) => {
          const active = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              className={`ptn-tab${active ? " ptn-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.value)}
            >
              <span className="ptn-tab__icon"><i className={`fa-solid ${tab.icon}`} /></span>
              <span>{!isSelf && tab.otherLabel ? tab.otherLabel : tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="p-3 p-md-4" style={sectionCardStyle}>
        {activeTab === "info" && (
          <div className="row g-3">
            {infoRows.map((row) => (
              <div className="col-12 col-md-6 col-xl-4" key={row.label}>
                <div className="ptn-info-card h-100" style={{ "--accent": row.accent }}>
                  <span className="ptn-info-card__icon"><i className={`fa-solid ${row.icon}`} /></span>
                  <div className="ptn-info-card__body">
                    <div className="ptn-info-card__label">{row.label}</div>
                    <div className="ptn-info-card__value">{row.render ? row.render() : row.value || "-"}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "attendance" && (
          <>
            <h5 className="fw-semibold mb-3">ประวัติการเข้างานล่าสุด</h5>
            <div className="table-responsive table-mobile-cards ptn-profile-cards">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium text-nowrap">วันที่</th>
                    <th className="text-secondary fw-medium text-center text-nowrap">เวลาเข้า</th>
                    <th className="text-secondary fw-medium text-center text-nowrap">เวลาออก</th>
                    <th className="text-secondary fw-medium text-center text-nowrap">รวมชั่วโมง</th>
                    <th className="text-secondary fw-medium text-center text-nowrap">สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.length > 0 ? (
                    attendance.map((item) => (
                      <tr key={item.uid}>
                        <td data-label="วันที่" className="text-nowrap">{format_date(item.attendance_date, "dd/mm/yyyy")}</td>
                        <td data-label="เวลาเข้า" className="text-center text-nowrap">{formatTime(item.check_in_at)}</td>
                        <td data-label="เวลาออก" className="text-center text-nowrap">{formatTime(item.check_out_at)}</td>
                        <td data-label="รวมชั่วโมง" className="text-center">{item.work_hours ? `${item.work_hours} ชม.` : "-"}</td>
                        <td data-label="สถานะ" className="text-center text-nowrap">{item.status || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-secondary py-5">ยังไม่มีประวัติการเข้างาน</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "leave" && (
          <>
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
              <h5 className="fw-semibold mb-0">{isSelf ? "วันลาของฉัน" : `วันลาของ ${fullName}`}</h5>
              <select className="form-select" style={{ width: 130, borderRadius: 12 }} value={year} onChange={(event) => setYear(event.target.value)}>
                {yearOptions.map((option) => (
                  <option key={option} value={option}>ปี {option}</option>
                ))}
              </select>
            </div>

            <div className="row g-3 mb-4">
              {(leaveSummary.types || [])
                .filter((type) => type.quota !== null)
                .map((type) => {
                  // แถบความคืบหน้า = สัดส่วนวันที่ใช้ไปจากโควตา
                  const usedRatio = type.quota > 0 ? Math.min(100, Math.round((type.used_days / type.quota) * 100)) : 0;
                  return (
                    <div className="col-6 col-xl-3" key={type.value}>
                      <div className="ptn-leave-card h-100">
                        <div className="ptn-leave-card__label">{type.label}</div>
                        <div className="ptn-leave-card__value">
                          {type.remaining_days}
                          <span>/ {type.quota} วัน</span>
                        </div>
                        <div className="ptn-leave-bar">
                          <span style={{ width: `${usedRatio}%` }} />
                        </div>
                        <div className="ptn-leave-card__meta">ใช้ไป {type.used_days} วัน</div>
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="table-responsive table-mobile-cards ptn-profile-cards">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium">รหัส</th>
                    <th className="text-secondary fw-medium text-nowrap">ประเภท</th>
                    <th className="text-secondary fw-medium text-nowrap">ช่วงวันที่</th>
                    <th className="text-secondary fw-medium text-center">จำนวนวัน</th>
                    <th className="text-secondary fw-medium text-center text-nowrap">สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.length > 0 ? (
                    leaves.map((item) => {
                      const style = LEAVE_STATUS_STYLE[item.status] || LEAVE_STATUS_STYLE.cancelled;
                      return (
                        <tr key={item.uid}>
                          <td data-label="รหัส" className="fw-semibold text-nowrap" style={{ color: "#0b4f84" }}>{item.leave_code || "-"}</td>
                          <td data-label="ประเภท" className="text-nowrap">{item.leave_type}</td>
                          <td data-label="ช่วงวันที่" className="text-nowrap">
                            {format_date(item.date_from, "dd/mm/yyyy")}
                            {item.date_to !== item.date_from ? ` - ${format_date(item.date_to, "dd/mm/yyyy")}` : ""}
                          </td>
                          <td data-label="จำนวนวัน" className="text-center fw-semibold">{item.total_days}</td>
                          <td data-label="สถานะ" className="text-center text-nowrap">
                            <span className="rounded-pill px-3 py-1 small fw-semibold text-nowrap d-inline-block" style={{ background: style.background, color: style.color }}>
                              {style.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-secondary py-5">ยังไม่มีประวัติการลาในปี {year}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "payslip" && (
          <>
            <h5 className="fw-semibold mb-3">สลิปเงินเดือนย้อนหลัง</h5>
            <div className="table-responsive table-mobile-cards ptn-profile-cards">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-secondary fw-medium text-nowrap">งวด</th>
                    <th className="text-secondary fw-medium text-nowrap">วันที่จ่าย</th>
                    <th className="text-secondary fw-medium text-end">รายได้รวม</th>
                    <th className="text-secondary fw-medium text-end">รายการหัก</th>
                    <th className="text-secondary fw-medium text-end">เงินรับสุทธิ</th>
                    <th className="text-secondary fw-medium text-center">สลิป</th>
                  </tr>
                </thead>
                <tbody>
                  {payslips.length > 0 ? (
                    payslips.map((item) => (
                      <tr key={item.uid}>
                        <td data-label="งวด" className="fw-semibold text-nowrap">{item.payroll?.period_month || "-"}</td>
                        <td data-label="วันที่จ่าย" className="text-nowrap">{item.payroll?.pay_date ? format_date(item.payroll.pay_date, "dd/mm/yyyy") : "-"}</td>
                        <td data-label="รายได้รวม" className="text-end">{toFixed(item.earnings_total)}</td>
                        <td data-label="รายการหัก" className="text-end" style={{ color: "#b63636" }}>{toFixed(item.deductions_total)}</td>
                        <td data-label="เงินรับสุทธิ" className="text-end fw-bold" style={{ color: "#188251" }}>{toFixed(item.net_total)}</td>
                        <td data-label="สลิป" className="text-center text-nowrap">
                          <button className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="ดูสลิป" onClick={() => setPreviewSlip(item)}>
                            <i className="fa-solid fa-eye" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger rounded-3" title="ดาวน์โหลด PDF" onClick={() => handleDownloadSlip(item)}>
                            <i className="fa-solid fa-file-pdf" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-secondary py-5">ยังไม่มีสลิปเงินเดือน (แสดงเฉพาะรอบที่อนุมัติแล้ว)</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "tax" && (
          <PersonalTaxPanel key={viewUid || "self"} hasEmployee={!!viewUid} employeeUid={isSelf ? null : viewUid} readOnly={!isSelf} />
        )}

        {activeTab === "finance-plan" && <FinancialPlanPanel />}
      </div>

      <Modal show={!!previewSlip} onHide={() => setPreviewSlip(null)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title as="h5" className="fw-semibold">สลิปเงินเดือน {previewSlip?.payroll?.period_month || ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#eef3f8" }}>
          <div style={{ overflowX: "auto" }}>
            {previewSlip && (
              <PayslipDocument ref={slipRef} company={company} payroll={previewSlip.payroll} item={previewSlip} />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setPreviewSlip(null)}>ปิด</button>
          <button className="btn btn-primary" onClick={() => downloadPayslipPdf(slipRef.current, `payslip_${previewSlip?.payroll?.period_month || ""}`)}>
            ดาวน์โหลด PDF
          </button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
}
